import { useEffect, useRef, type CSSProperties } from 'react';
import { fmt, heat } from '@balansa/shared';
import { IconChev, IconEdit, IconLink } from '../lib/icons';
import { useApp, useWheel } from '../store';

export function AreasTab() {
  const { idx } = useWheel();
  const openDialog = useApp((s) => s.openDialog);
  const setHover = useApp((s) => s.setHover);
  return (
    <>
      <div id="tree" className="tree" onMouseLeave={() => setHover(null, null)}>
        {idx.tops.length ? idx.tops.map((id) => <TreeNode key={id} id={id} />) : <p className="empty">Vēl nav nevienas jomas.</p>}
      </div>
      <button type="button" className="btn sm add-top" onClick={() => openDialog({ kind: 'node', mode: 'new', parentId: null })}>
        + Pievienot jomu
      </button>
    </>
  );
}

function TreeNode({ id }: { id: string }) {
  const { wheel, idx } = useWheel();
  const selected = useApp((s) => s.ui.selected);
  const collapsed = useApp((s) => s.ui.collapsed);
  const tab = useApp((s) => s.ui.tab);
  const A = useApp.getState;
  const rowRef = useRef<HTMLDivElement>(null);
  const n = idx.node.get(id)!;
  const kids = idx.kids.get(id)!;
  const v = idx.val.get(id)!;
  const d = idx.depth.get(id)!;
  const leaf = !kids.length;
  const open = !collapsed.has(id);
  const low = v <= wheel.settings.threshold;
  const sel = selected === id;

  useEffect(() => {
    if (sel && tab === 'areas') rowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [sel, tab]);

  return (
    <div className={'tnode' + (d === 0 ? ' lvl0' : '')} style={{ '--c': idx.color(id) } as CSSProperties}>
      <div
        ref={rowRef}
        className={'trow' + (sel ? ' sel' : '') + (low ? ' low' : '')}
        data-row={id}
        style={{ paddingLeft: 6 + d * 18 }}
        onMouseEnter={() => A().setHover(id, null)}
      >
        {kids.length ? (
          <button type="button" className="twisty" aria-expanded={open} aria-label={open ? 'Sakļaut' : 'Izvērst'} onClick={() => A().toggleCollapse(id)}>
            <IconChev />
          </button>
        ) : (
          <span className="twisty-sp" />
        )}
        {d === 0 ? <span className="chip" /> : <span className="pip" />}
        <button type="button" className="tname" title="Izvēlēties uz apļa" onClick={() => A().pick(id)}>
          {n.name}
        </button>
        {leaf ? (
          <input
            type="range"
            min={0}
            max={10}
            step={1}
            value={Math.round(n.value)}
            style={{ '--p': n.value * 10 + '%' } as CSSProperties}
            aria-label={n.name + ' — vērtība'}
            onChange={(e) => A().setNodeValue(id, +e.target.value)}
          />
        ) : (
          <span className="meter" title="Apakšjomu vidējais">
            <i style={{ width: v * 10 + '%', background: heat(v) }} />
          </span>
        )}
        <span className="tval" data-testid={'val-' + n.name}>
          {fmt(v)}
        </span>
        <span className="tacts">
          <button type="button" className="icon-btn" title="Jauna saite no šīs jomas" aria-label="Jauna saite no šīs jomas" onClick={() => A().openDialog({ kind: 'link', from: id })}>
            <IconLink />
          </button>
          <button type="button" className="icon-btn" title="Pievienot apakšjomu" aria-label="Pievienot apakšjomu" onClick={() => A().openDialog({ kind: 'node', mode: 'new', parentId: id })}>
            +
          </button>
          <button type="button" className="icon-btn" title="Rediģēt" aria-label="Rediģēt" onClick={() => A().openDialog({ kind: 'node', mode: 'edit', id })}>
            <IconEdit />
          </button>
        </span>
      </div>
      {n.note ? (
        <div className="tnote" style={{ paddingLeft: 62 + d * 18 }}>
          {n.note}
        </div>
      ) : null}
      {kids.length && open ? (
        <div className="tkids">
          {kids.map((k) => (
            <TreeNode key={k} id={k} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
